import { expandOsGuestOriginAliases } from "./embed";

/** ICRC-29 signer entry — current OISY host (not legacy oisy.com/sign). */
export const OISY_SIGNER_URL =
  (typeof import.meta !== "undefined" &&
    (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_OISY_SIGNER_URL?.trim()) ||
  "https://signer.oisy.com";

export const OISY_SIGNER_LEGACY_URL = "https://legacy-signer.oisy.com";

export const OISY_SIGNER_WINDOW_FEATURES =
  "toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=520,height=720";

/** Guest iframe running inside Bonsai OS shell. SPA routes often drop `?embed=os`. */
export function isOsEmbeddedGuest(
  _search = typeof window !== "undefined" ? window.location.search : "",
): boolean {
  if (typeof window === "undefined") return false;
  return window.parent !== window;
}

/** Pop-out hub window (`?embed=os&popout=1`) — uses window.opener for OS relay. */
export function isOsPopoutGuest(search = typeof window !== "undefined" ? window.location.search : ""): boolean {
  const raw = search.startsWith("?") ? search : search ? `?${search}` : "";
  const params = new URLSearchParams(raw);
  return params.get("embed") === "os" && params.get("popout") === "1";
}

export function osHostRelayTarget(): Window | null {
  if (typeof window === "undefined") return null;
  if (isOsPopoutGuest()) {
    if (window.opener && !window.opener.closed) return window.opener;
    // Safari / in-app browsers sometimes drop opener — cannot SSO; fall through to standalone auth.
    return null;
  }
  if (window.parent !== window) return window.parent;
  return null;
}

export function isOsHostedGuest(search = typeof window !== "undefined" ? window.location.search : ""): boolean {
  if (isOsPopoutGuest(search)) {
    return typeof window !== "undefined" && !!window.opener && !window.opener.closed;
  }
  return isOsEmbeddedGuest(search);
}

/**
 * OISY must authenticate on the Bonsai OS host origin (popup / ICRC-29).
 * signer.oisy.com blocks iframes (X-Frame-Options: DENY).
 */
export function shouldDelegateOisyToOsHost(search?: string): boolean {
  return isOsHostedGuest(search);
}

/** Never embed signer.oisy.com in an iframe — always use browser popup/tab. */
export function preferEmbeddedOisySigner(): boolean {
  return false;
}

export type OsWalletRelayOriginsInput = {
  marketBaseUrl?: string;
  radioBaseUrl?: string;
  cloudBaseUrl?: string;
  /** Bonsai Tube assets origin (Hub iframe guest). */
  tubeBaseUrl?: string;
  /** Bonsai Works assets origin (Hub iframe guest). */
  worksBaseUrl?: string;
  /** Canopy wallet assets origin (Bazaar / Hub iframe guest). */
  canopyBaseUrl?: string;
  /** Extra admin-curated guest origins. */
  extra?: string[];
};

export function normalizeOsOrigin(origin: string): string {
  return origin.replace(/\/$/, "");
}

/** Allowed postMessage origins for OS wallet host relays (OISY, multichain, II). */
export function buildOsWalletRelayOrigins(input: OsWalletRelayOriginsInput): string[] {
  const out = new Set<string>();
  for (const raw of [
    input.marketBaseUrl,
    input.radioBaseUrl,
    input.cloudBaseUrl,
    input.tubeBaseUrl,
    input.worksBaseUrl,
    input.canopyBaseUrl,
    ...(input.extra ?? []),
  ]) {
    const trimmed = raw?.trim();
    if (!trimmed) continue;
    // Include both gateway spellings so handshake / wallet-sync is not dropped.
    for (const alias of expandOsGuestOriginAliases(normalizeOsOrigin(trimmed))) {
      out.add(alias);
    }
  }
  return [...out];
}
